import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import ListingCard from "@/components/listings/ListingCard";
import FiltersSidebar from "@/components/listings/FiltersSidebar";
import { ChevronRight, Home as HomeIcon, Loader2 } from "lucide-react";
import { fetchListings } from "@/lib/api";

function toParams(f, category) {
  const p = { category };
  if (f.subcategory) p.subcategory = f.subcategory;
  if (f.county) p.county = f.county;
  if (f.city) p.city = f.city;
  if (f.minPrice) p.min = f.minPrice;
  if (f.maxPrice) p.max = f.maxPrice;
  if (f.conditions?.length) p.conditions = f.conditions.join(",");
  if (f.onlyFeatured) p.featured = 1;
  if (f.sort) p.sort = f.sort;
  p.limit = 48;
  return p;
}

export default function CategoryPage() {
  const { categoryId } = useParams();
  const { categories } = useApp();
  const cat = categories.find((c) => c.id === categoryId);

  const [filters, setFilters] = useState({
    county: "", city: "", subcategory: "", minPrice: "", maxPrice: "",
    conditions: [], onlyFeatured: false, sort: "newest",
  });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!cat) return;
    setLoading(true);
    fetchListings(toParams(filters, cat.id))
      .then((d) => setItems(d.items || []))
      .finally(() => setLoading(false));
  }, [cat, filters]);

  if (!cat && categories.length > 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-16 text-center">
        <h1 className="text-2xl font-black text-white">Categorie inexistentă</h1>
        <Link to="/" className="amd-link mt-4 inline-block">← Înapoi acasă</Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8 fade-in">
      <Breadcrumb items={[{ to: "/", label: <HomeIcon className="w-3.5 h-3.5" /> }, { label: cat?.name || "…" }]} />
      <h1 className="mt-3 text-2xl md:text-3xl font-black text-white">{cat?.name || "…"}</h1>
      <p className="mt-1 text-sm" style={{ color: "var(--amd-text-muted)" }}>{loading ? "se caută…" : `${items.length} anunțuri`}</p>

      <div className="mt-6 flex flex-col lg:flex-row gap-6">
        <FiltersSidebar filters={filters} setFilters={setFilters} subcategories={cat?.subcategories || []} />
        <div className="flex-1 min-w-0">
          {loading ? (
            <div className="py-16 text-center"><Loader2 className="w-7 h-7 mx-auto animate-spin" style={{ color: "var(--amd-primary)" }} /></div>
          ) : items.length === 0 ? (
            <div className="amd-card rounded-lg p-10 text-center">
              <div className="font-bold text-white text-lg">Niciun rezultat</div>
              <p className="mt-1 text-sm" style={{ color: "var(--amd-text-muted)" }}>Încearcă să modifici filtrele.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
              {items.map((l) => <ListingCard key={l.id} listing={l} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Breadcrumb({ items }) {
  return (
    <nav className="flex items-center text-xs gap-1" style={{ color: "var(--amd-text-muted)" }}>
      {items.map((it, i) => (
        <React.Fragment key={i}>
          {it.to ? <Link to={it.to} className="amd-link inline-flex items-center">{it.label}</Link> : <span className="text-white">{it.label}</span>}
          {i < items.length - 1 && <ChevronRight className="w-3 h-3 opacity-60" />}
        </React.Fragment>
      ))}
    </nav>
  );
}
